import { useMemo, useState } from "react";
import { useGetOrganizationStructure } from "./query";
import { configuredLevelKeysOf, resolveLevels } from "../utils/breakdownLevel";

/**
 * ESG-3960: the dimension level an Insights drill-down table breaks down into.
 *
 * Auto-resolves to the level below the user's deepest filter, and lets the user override it from a
 * dropdown. The page's dimension filter still applies on top — level and filter are independent request
 * params which the backend ANDs, so "break down by Country while filtering Division = Logistics" returns
 * the correct per-country Logistics figure.
 *
 * The override exists because auto-resolution can land on an unusable level: the staging org's
 * level-four "Unit" dimension has 1,807 items, so filtering down to Subdivision automatically produced a
 * 1,807-row table. Every configured level is offered, including levels at or above the current filter.
 *
 * Previously copy-pasted across all four drill-downs (three as `resolveLevels` helpers, one inlined),
 * with the selector wired into only two of them.
 */
export function useBreakdownLevel(dimension) {
  const { data: organizationStructure } = useGetOrganizationStructure();

  const configuredLevelKeys = useMemo(
    () => configuredLevelKeysOf(organizationStructure),
    [organizationStructure]
  );

  const { next: autoLevelKey } = resolveLevels(dimension, configuredLevelKeys);

  // null = follow auto-resolution. Not reset when the filter changes: an explicit choice is the user's,
  // and silently reverting it as they drill would make the dropdown feel broken.
  const [selectedLevelKey, setSelectedLevelKey] = useState(null);
  const levelKey = selectedLevelKey || autoLevelKey;
  const levelDimension = levelKey ? organizationStructure?.dimensions?.[levelKey] : null;

  const levelOptions = useMemo(
    () =>
      configuredLevelKeys.map((k) => ({
        key: k,
        name: organizationStructure?.dimensions?.[k]?.name || k,
      })),
    [configuredLevelKeys, organizationStructure]
  );

  return {
    // Returned so pages that also need the raw structure (e.g. for SourceDataTasks) don't fire a second
    // useGetOrganizationStructure query.
    organizationStructure,
    configuredLevelKeys,
    autoLevelKey,
    // The level in effect: the user's choice, else auto.
    levelKey,
    levelDimension,
    levelName: levelDimension?.name || null,
    levelOptions,
    selectedLevelKey: levelKey,
    onSelectLevel: setSelectedLevelKey,
    // Hide the by-dimension views only when the org has no dimensions at all. A filter sitting at the
    // lowest level leaves no AUTO level, but the dropdown is still usable, so the table stays.
    hideByDimension: !levelKey && configuredLevelKeys.length === 0,
  };
}
